import { useQueryClient } from '@tanstack/react-query';
import { qk } from '@/api/queries';
import { useWsEvent } from '@/realtime';
import { toast } from '@/stores/toasts';
import { seriesLabel } from '@/features/series/series';
import { slotTime } from './links';

/**
 * v9 area A in-app notifications (mounted once, signed in): a direct invite
 * from a connection (K1), personal-link requests (the host is asked; the
 * requester hears back), weekly date night proposals and (v12.1) profile views. Each event also
 * refreshes its list.
 */
export function HabitRealtime() {
  const qc = useQueryClient();

  useWsEvent('date_invite.updated', ({ invite, direct }) => {
    if (direct && invite?.role === 'guest') {
      toast.people(`${invite.host.display_name} invited you to a date`, 'Open Friends, then Dates, to plan it.');
    }
  });

  useWsEvent('link_request.updated', ({ request }) => {
    void qc.invalidateQueries({ queryKey: qk.linkRequests });
    void qc.invalidateQueries({ queryKey: qk.myLink });
    if (!request) return;
    if (request.role === 'host' && request.status === 'pending') {
      toast.people(`${request.requester.display_name} asked for a date`, `${new Date(request.starts_at).toLocaleDateString(undefined, { weekday: 'short' })} ${slotTime(request.starts_at)}. Swipe in Requests.`);
    }
    if (request.role === 'requester' && request.status === 'approved') {
      void qc.invalidateQueries({ queryKey: qk.dateInvites });
      toast.success(`It’s a date with ${request.host.display_name}!`, 'We’ve emailed you a calendar invite.');
    }
  });

  // v12.1: someone opened your link (named unless your own private browsing is on).
  useWsEvent('profile_view.new', ({ viewer }) => {
    void qc.invalidateQueries({ queryKey: qk.profileViews });
    toast.people(viewer ? `${viewer.viewer.display_name.split(' ')[0]} viewed your profile` : 'Someone viewed your profile', 'See who in Requests → Viewed you.');
  });

  useWsEvent('date_series.updated', ({ series }) => {
    void qc.invalidateQueries({ queryKey: qk.dateSeries });
    void qc.invalidateQueries({ queryKey: qk.dateInvites });
    if (series?.role === 'partner' && series.status === 'pending') {
      toast.people(`${series.with.display_name} wants to make it a weekly thing`, seriesLabel(series));
    }
  });

  return null;
}
